import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Filter, Trash2, ExternalLink, X } from "lucide-react";
import { api, formatApiError } from "@/lib/apiClient";

const CATEGORIES = [
  { value: "complaint",     label: "Complaint" },
  { value: "product_issue", label: "Product Issue" },
  { value: "service",       label: "Service Feedback" },
  { value: "suggestion",    label: "Suggestion" },
  { value: "improvement",   label: "Improvement Idea" },
  { value: "feedback",      label: "General Feedback" },
];
const STATUSES = [
  { value: "new",       label: "New",        tone: "border-amber-300 bg-amber-50 text-amber-700" },
  { value: "in_review", label: "In Review",  tone: "border-blue-200 bg-blue-50 text-blue-700" },
  { value: "resolved",  label: "Resolved",   tone: "border-emerald-300 bg-emerald-50 text-emerald-700" },
  { value: "closed",    label: "Closed",     tone: "border-brand-charcoal/30 bg-brand-muted text-brand-charcoal/70" },
];
const catLabel = (k) => (CATEGORIES.find((c) => c.value === k) || {}).label || k;
const statusMeta = (k) => STATUSES.find((s) => s.value === k) || STATUSES[0];

export default function AdminComplaints() {
  const [items, setItems] = useState([]);
  const [filterCat, setFilterCat] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [open, setOpen] = useState(null);

  const load = () => {
    const params = {};
    if (filterCat !== "all") params.category = filterCat;
    if (filterStatus !== "all") params.status = filterStatus;
    api.get("/admin/complaints", { params }).then((r) => setItems(r.data || []));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCat, filterStatus]);

  const updateStatus = async (cid, status) => {
    try {
      await api.patch(`/admin/complaints/${cid}/status`, { status });
      toast.success("Status updated");
      load();
      if (open && open.id === cid) setOpen({ ...open, status });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveNotes = async () => {
    if (!open) return;
    try {
      await api.patch(`/admin/complaints/${open.id}/status`, { status: open.status, admin_notes: open.admin_notes });
      toast.success("Notes saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (cid) => {
    if (!window.confirm("Delete this entry?")) return;
    try { await api.delete(`/admin/complaints/${cid}`); toast.success("Deleted"); load(); setOpen(null); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const counts = useMemo(() => {
    const c = { all: items.length };
    STATUSES.forEach((s) => { c[s.value] = items.filter((i) => i.status === s.value).length; });
    return c;
  }, [items]);

  return (
    <div data-testid="admin-complaints" className="space-y-8">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">Complaints &amp; Feedback</div>
          <h1 className="editorial-h1 text-5xl mt-2">{items.length} entries.</h1>
        </div>
        <div className="flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55"><Filter size={12} /> Category</div>
          <select className="pp-input bg-transparent" data-testid="ac-filter-cat" value={filterCat} onChange={(e) => setFilterCat(e.target.value)} style={{ width: "auto" }}>
            <option value="all">All</option>
            {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <select className="pp-input bg-transparent" data-testid="ac-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={{ width: "auto" }}>
            <option value="all">All statuses</option>
            {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      </header>

      {/* Status stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <button onClick={() => setFilterStatus("all")} className={`card-elevated p-4 text-left transition-all ${filterStatus === "all" ? "ring-2 ring-brand-charcoal" : ""}`}>
          <div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Total</div>
          <div className="font-display text-3xl mt-1">{counts.all}</div>
        </button>
        {STATUSES.map((s) => (
          <button key={s.value} data-testid={`ac-card-${s.value}`} onClick={() => setFilterStatus(s.value)}
            className={`card-elevated p-4 text-left transition-all ${filterStatus === s.value ? "ring-2 ring-brand-charcoal" : ""}`}>
            <div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">{s.label}</div>
            <div className="font-display text-3xl mt-1">{counts[s.value] || 0}</div>
          </button>
        ))}
      </div>

      {/* Table */}
      <div className="card-elevated overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-[11px] uppercase tracking-[0.22em] text-brand-charcoal/55">
            <tr className="border-b border-brand-line">
              <th className="p-4">When</th><th className="p-4">From</th><th className="p-4">Category</th><th className="p-4">Subject</th><th className="p-4">Status</th><th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const sm = statusMeta(it.status);
              return (
                <tr key={it.id} data-testid={`ac-row-${it.id}`} className="border-b border-brand-line last:border-0 hover:bg-brand-muted/40">
                  <td className="p-4 text-xs text-brand-charcoal/65 align-top whitespace-nowrap">{(it.created_at || "").slice(0, 10)}</td>
                  <td className="p-4 align-top">
                    <div className="font-medium">{it.name}</div>
                    <div className="text-xs text-brand-charcoal/55">{it.phone}</div>
                    {it.email && <div className="text-xs text-brand-charcoal/55">{it.email}</div>}
                  </td>
                  <td className="p-4 align-top text-xs uppercase tracking-[0.18em] text-brand-red">{catLabel(it.category)}</td>
                  <td className="p-4 align-top max-w-md">
                    <div className="font-medium">{it.subject}</div>
                    <div className="text-xs text-brand-charcoal/65 line-clamp-2 mt-1">{it.description}</div>
                  </td>
                  <td className="p-4 align-top">
                    <span className={`text-[10px] uppercase tracking-[0.18em] border rounded-full px-2 py-0.5 ${sm.tone}`}>{sm.label}</span>
                  </td>
                  <td className="p-4 align-top">
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => setOpen(it)} data-testid={`ac-open-${it.id}`} className="btn-pill btn-ghost text-xs border border-brand-line">Open</button>
                      <select data-testid={`ac-status-${it.id}`} value={it.status} onChange={(e) => updateStatus(it.id, e.target.value)} className="text-xs border border-brand-line rounded-full px-3 py-1 bg-transparent">
                        {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                  </td>
                </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan="6" className="p-12 text-center text-brand-charcoal/60">No entries match the current filter.</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-brand-charcoal/40 grid place-items-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-[11px] uppercase tracking-[0.22em] text-brand-red">{catLabel(open.category)}</div>
                <div className="font-display text-3xl mt-1">{open.subject}</div>
              </div>
              <button onClick={() => setOpen(null)}><X /></button>
            </div>
            <div className="grid sm:grid-cols-3 gap-3 text-sm">
              <div><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">From</div><div className="mt-1 font-medium">{open.name}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Phone</div><div className="mt-1">{open.phone || "—"}</div></div>
              <div><div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Email</div><div className="mt-1">{open.email || "—"}</div></div>
            </div>
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Description</div>
              <p className="mt-2 text-sm whitespace-pre-line text-brand-charcoal/85 leading-relaxed">{open.description}</p>
            </div>
            {open.image_url && (
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Attachment</div>
                <a href={open.image_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-2 text-sm font-medium hover:text-brand-red">Open image <ExternalLink size={12} /></a>
              </div>
            )}
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Admin notes (internal)</div>
              <textarea rows="4" className="pp-input mt-2" value={open.admin_notes || ""} onChange={(e) => setOpen({ ...open, admin_notes: e.target.value })} data-testid="ac-notes" />
            </div>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-[0.22em] text-brand-charcoal/55">Status</label>
                <select className="pp-input bg-transparent mt-2" value={open.status} onChange={(e) => updateStatus(open.id, e.target.value)} data-testid="ac-open-status">
                  {STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </div>
              <div className="flex items-end gap-2">
                {open.email && <a href={`mailto:${open.email}?subject=Re: ${encodeURIComponent(open.subject)}`} className="btn-pill btn-outline-dark text-sm">Email back</a>}
                {open.phone && <a href={`https://wa.me/91${(open.phone || "").replace(/[^0-9]/g, "")}`} target="_blank" rel="noreferrer" className="btn-pill btn-primary text-sm">WhatsApp</a>}
              </div>
            </div>
            <div className="mt-8 flex justify-between">
              <button onClick={() => del(open.id)} className="btn-pill btn-ghost text-sm text-brand-red"><Trash2 size={14} /> Delete</button>
              <div className="flex gap-2">
                <button onClick={() => setOpen(null)} className="btn-pill btn-ghost text-sm">Close</button>
                <button onClick={saveNotes} className="btn-pill btn-dark text-sm" data-testid="ac-save-notes">Save notes</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
